import { useMemo } from "react";

import { type MoneyInput } from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";

import getCartUpsellBundlesCollectionQuery from "@graphql/get-cart-upsell-bundles-collection.graphql";
import { storefrontAPIFetcher } from "@react/hooks/fetchers";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import { camelize } from "@react/utilities";
import { STOREFRONT_API_URL } from "@tablescape/constants";
import {
  type CartProductVariant,
  type CartProduct,
  type CartProductBundle,
  type CartProductConnection,
  type CartSectionSettings,
} from "@tablescape/state";
import {
  formatProduct,
  mapIntelligemsPriceToVariant,
} from "@tablescape/utilities";

type ProductBundleNode = {
  metafield: {
    reference: {
      badgeText: {
        value: string | null;
      } | null;
      discount: {
        value: string | null;
      } | null;
      productBundle: {
        references: {
          nodes: CartProductConnection[];
        };
      };
    };
    value: string;
  } | null;
};

export type ProductBundlesAPIConnection = {
  data?: {
    collection?: {
      products?: {
        nodes?: ProductBundleNode[];
      };
    };
  };
};

function mapDiscountToVariant(
  variant: CartProductVariant,
  discount: MoneyInput,
  itemCount: number
) {
  if (!variant.compareAtPrice) {
    return {
      ...variant,
      compareAtPrice: variant.price,
      price: {
        ...variant.price,
        amount: (
          parseFloat(variant.price.amount) -
          Math.floor(parseFloat(discount.amount) / itemCount)
        ).toFixed(1),
      },
    };
  }

  return variant;
}

/**
 * Calculates the discount for a given product based on the number of items in the cart and the discount amount.
 * The total discount is then dispersed evenly across all remaining products in the bundle.
 * @param {Object} params - The parameters object.
 * @param {CartProduct} params.product - The product to calculate the discount for.
 * @param {MoneyInput} params.discount - The discount amount.
 * @param {number} params.itemCount - The number of items in the cart.
 * @returns {CartProduct} The updated product with the discount applied.
 */
function calculateDiscount({
  discount,
  itemCount,
  product,
}: {
  discount: MoneyInput;
  itemCount: number;
  product: CartProduct;
}) {
  return {
    ...product,
    variants: product.variants
      .map((variant) => mapIntelligemsPriceToVariant(variant))
      .map((variant) => mapDiscountToVariant(variant, discount, itemCount)),
  };
}

/**
 * A custom hook that fetches and returns product bundles data from the Shopify Storefront API.
 * @returns An object containing an array of CartProductBundle objects or undefined if data is not available.
 */
export function useProductBundles() {
  const {
    meta: {
      localization: { country },
    },
  } = useSectionSettings<CartSectionSettings>();
  const { data, isLoading } = useSWR<ProductBundlesAPIConnection>(
    [STOREFRONT_API_URL, getCartUpsellBundlesCollectionQuery, { country }],
    storefrontAPIFetcher,
    {}
  );

  /**
   * Returns an array of product bundles with their respective discounts and badge text.
   * @param data - The data object containing the collection products.
   * @returns An array of product bundles with their respective discounts and badge text.
   */
  const productBundles = useMemo(() => {
    if (isLoading) {
      return undefined;
    }

    if (!data?.data?.collection?.products?.nodes?.length) {
      return [];
    }

    return data.data.collection.products.nodes
      .map(({ metafield }) => {
        if (!metafield?.reference) {
          return null;
        }

        const {
          reference: { badgeText, discount, productBundle },
        } = metafield;

        const parsedDiscount = discount?.value
          ? camelize<MoneyInput>(JSON.parse(discount?.value))
          : null;

        return {
          badgeText: badgeText?.value ?? null,
          discount: parsedDiscount,
          products: productBundle.references.nodes.map((node) => {
            const formattedProduct = formatProduct(node);

            return parsedDiscount
              ? calculateDiscount({
                  product: formattedProduct,
                  discount: parsedDiscount,
                  itemCount: Math.max(
                    productBundle.references.nodes.length - 1,
                    1
                  ),
                })
              : formattedProduct;
          }),
        };
      })
      .filter((bundle): bundle is CartProductBundle => bundle !== null);
  }, [data, isLoading]) satisfies CartProductBundle[] | undefined;

  return {
    productBundles,
  };
}
